import { useState, useEffect, useRef, useCallback } from 'react';

/*
 * Shared slider logic (SPEC §8): current index, next/prev/goTo, optional
 * auto-advance with pause-on-hover, and reduced-motion awareness (auto-advance
 * is disabled when the user prefers reduced motion).
 *
 *   count    — number of slides/pages
 *   autoMs   — auto-advance interval in ms (0 = off)
 */
export default function useSlider(count, autoMs = 0) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Keep index in range if count shrinks.
  useEffect(() => {
    if (index > count - 1) setIndex(0);
  }, [count, index]);

  useEffect(() => {
    if (!autoMs || paused || count <= 1) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, autoMs);
    return () => clearInterval(timer.current);
  }, [autoMs, paused, count]);

  const hoverHandlers = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onFocusCapture: () => setPaused(true),
    onBlurCapture: () => setPaused(false),
  };

  return { index, setIndex: goTo, next, prev, goTo, hoverHandlers };
}
